/**
 * Friction-loaded confirmation dialog for permanently deleting the
 * SubContractorHub integration. Story SCH-1.5 (FR6, NFR12).
 *
 * The user must type DELETE (case-sensitive) to enable the destructive
 * action button. While the mutation is in flight, the dialog stays open
 * with a spinner, cancel + backdrop close are blocked — same UX pattern
 * established in SCH-1.4 to handle Fly cold-start latency.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

import { useDeleteSubContractorHubIntegration } from '@/hooks/integrations/useSubContractorHubConnection';

const CONFIRM_PHRASE = 'DELETE';

interface DeleteIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Note: Epic 3 will introduce mapping-rule and imported-project counts
// to display in the destructive copy. Add an `integration` prop back at
// that point so the dialog can render the counts. Story 1.5 deliberately
// keeps the prop surface minimal until those tables/columns exist.

export function DeleteIntegrationDialog({
  open,
  onOpenChange,
}: DeleteIntegrationDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const { toast } = useToast();
  const deleteMutation = useDeleteSubContractorHubIntegration();

  const isPending = deleteMutation.isPending;
  const canConfirm = confirmText === CONFIRM_PHRASE && !isPending;
  const showHint = confirmText.length > 0 && confirmText !== CONFIRM_PHRASE;

  const close = () => {
    setConfirmText('');
    onOpenChange(false);
  };

  const handleConfirm = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          toast({
            title: 'Could not delete integration',
            description: 'Please try again.',
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Integration deleted' });
        }
        close();
      },
      onError: () => {
        toast({
          title: 'Could not delete integration',
          description: 'Please try again.',
          variant: 'destructive',
        });
        close();
      },
    });
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        // Block backdrop / Escape close while the mutation is in flight.
        if (!isPending) {
          if (!o) setConfirmText('');
          onOpenChange(o);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete SubContractorHub integration?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 text-sm text-muted-foreground">
            <span className="block">
              This permanently removes your SubContractorHub API key, all field /
              stage / adder / document mappings, and sync history.
            </span>
            <span className="block">
              Imported projects in Joules will remain — they keep all their
              imported data and a historical &ldquo;Imported from
              SubContractorHub&rdquo; badge.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="delete-confirm-input">
            Type <strong>{CONFIRM_PHRASE}</strong> to confirm
          </Label>
          <Input
            id="delete-confirm-input"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={isPending}
          />
          {showHint && (
            <p className="text-xs text-muted-foreground">
              Type {CONFIRM_PHRASE} exactly to enable the button.
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={!canConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
